import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { RootLayout } from './layouts/RootLayout';
import { DashboardLayout } from './layouts/DashboardLayout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';

// Eager: first paint (login) and the catch-all.
import { LoginPage } from './pages/auth/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';

// Eager: the entire EnviroLite surface. Inspectors work offline in the field —
// keeping lite in the main bundle means the PWA shell needs no follow-up
// chunk fetches to run the inspection flow.
import { LiteLayout } from './pages/lite/LiteLayout';
import { LiteDashboard } from './pages/lite/LiteDashboard';
import { LiteSitesPage } from './pages/lite/LiteSitesPage';
import { LiteSiteDetailPage } from './pages/lite/LiteSiteDetailPage';
import { LiteStartInspectionPage } from './pages/lite/LiteStartInspectionPage';
import { LiteInspectionFlowPage } from './pages/lite/LiteInspectionFlowPage';
import { LiteManualUploadPage } from './pages/lite/LiteManualUploadPage';
import { LiteSchedulePage } from './pages/lite/LiteSchedulePage';
import { LiteMapsPage } from './pages/lite/LiteMapsPage';
import { LiteLearnPage } from './pages/lite/LiteLearnPage';

// Everything else is code-split per page so lite users never download the
// admin dashboard (and hub users don't pay for pages they don't visit).
// Suspense fallbacks live at the layout <Outlet>s, so the shell/sidebar stays
// mounted while a page chunk loads.
const lazyNamed = (
  loader: () => Promise<Record<string, unknown>>,
  name: string
) =>
  lazy(async () => ({
    default: (await loader())[name] as React.ComponentType,
  }));

// Auth
const SignupPage = lazyNamed(
  () => import('./pages/auth/SignupPage'),
  'SignupPage'
);
const EmailVerificationPage = lazyNamed(
  () => import('./pages/auth/EmailVerificationPage'),
  'EmailVerificationPage'
);
const PendingApprovalPage = lazyNamed(
  () => import('./pages/auth/PendingApprovalPage'),
  'PendingApprovalPage'
);
const OIDCCallbackPage = lazyNamed(
  () => import('./pages/auth/OIDCCallbackPage'),
  'OIDCCallbackPage'
);
const ModeSelectionPage = lazyNamed(
  () => import('./pages/auth/ModeSelectionPage'),
  'ModeSelectionPage'
);
const AcceptInvitePage = lazyNamed(
  () => import('./pages/auth/AcceptInvitePage'),
  'AcceptInvitePage'
);
const ForgotPasswordPage = lazyNamed(
  () => import('./pages/auth/ForgotPasswordPage'),
  'ForgotPasswordPage'
);
const ResetPasswordPage = lazyNamed(
  () => import('./pages/auth/ResetPasswordPage'),
  'ResetPasswordPage'
);

// JIM-0924-02: external-user access tunnel + where they land afterwards.
const SiteAccessStartPage = lazyNamed(
  () => import('./pages/access/SiteAccessStartPage'),
  'SiteAccessStartPage'
);
const SiteAccessSetupPage = lazyNamed(
  () => import('./pages/access/SiteAccessSetupPage'),
  'SiteAccessSetupPage'
);
const SiteAccessFindPage = lazyNamed(
  () => import('./pages/access/SiteAccessFindPage'),
  'SiteAccessFindPage'
);
const ClientHomePage = lazyNamed(
  () => import('./pages/client/ClientHomePage'),
  'ClientHomePage'
);

// Hub
const DashboardPage = lazyNamed(
  () => import('./pages/dashboard/DashboardPage'),
  'DashboardPage'
);
const Inspections = lazyNamed(
  () => import('./pages/Inspections'),
  'Inspections'
);
const InspectionRecordPage = lazyNamed(
  () => import('./pages/InspectionRecordPage'),
  'InspectionRecordPage'
);
const InspectionLiteHandoff = lazyNamed(
  () => import('./pages/InspectionLiteHandoff'),
  'InspectionLiteHandoff'
);
const SitesPage = lazyNamed(
  () => import('./pages/sites/SitesPage'),
  'SitesPage'
);
const SiteDetailPage = lazyNamed(
  () => import('./pages/sites/SiteDetailPage'),
  'SiteDetailPage'
);
const NewSite = lazyNamed(() => import('./pages/sites/NewSite'), 'NewSite');
const OrganizationsPage = lazyNamed(
  () => import('./pages/organizations/OrganizationsPage'),
  'OrganizationsPage'
); // Clients page (renamed)
const ClientDetailPage = lazyNamed(
  () => import('./pages/organizations/OrganizationDetailPage'),
  'ClientDetailPage'
);
const SchedulePage = lazyNamed(
  () => import('./pages/schedule/SchedulePage'),
  'SchedulePage'
);
const ReportsPage = lazyNamed(
  () => import('./pages/reports/ReportsPage'),
  'ReportsPage'
);
const DataQualityPage = lazyNamed(
  () => import('./pages/reports/DataQualityPage'),
  'DataQualityPage'
);
const SetupWorklistPage = lazyNamed(
  () => import('./pages/reports/SetupWorklistPage'),
  'SetupWorklistPage'
);
const PermitExpirationsPage = lazyNamed(
  () => import('./pages/reports/PermitExpirationsPage'),
  'PermitExpirationsPage'
);
const SettingsPage = lazyNamed(
  () => import('./pages/settings/SettingsPage'),
  'SettingsPage'
);
const InviteUserPage = lazyNamed(
  () => import('./pages/admin/InviteUserPage'),
  'InviteUserPage'
);
const InvitationsListPage = lazyNamed(
  () => import('./pages/admin/InvitationsListPage'),
  'InvitationsListPage'
);
const AdminPortal = lazyNamed(
  () => import('./pages/admin/AdminPortal'),
  'AdminPortal'
);
const PeopleHubPage = lazyNamed(
  () => import('./pages/admin/PeopleHubPage'),
  'PeopleHubPage'
);
const FinancialDashboard = lazyNamed(
  () => import('./pages/financials/FinancialDashboard'),
  'FinancialDashboard'
);
const ConsultantDashboard = lazyNamed(
  () => import('./pages/consultant/ConsultantDashboard'),
  'ConsultantDashboard'
);
const ClientManagementPage = lazyNamed(
  () => import('./pages/clientManagement/ClientManagementPage'),
  'ClientManagementPage'
);
const SWPPPPlanEditorPage = lazyNamed(
  () => import('./pages/admin/swppp-plan/SWPPPPlanEditorPage'),
  'SWPPPPlanEditorPage'
);
const DataReconInboxPage = lazyNamed(
  () => import('./pages/recon/DataReconInboxPage'),
  'DataReconInboxPage'
);

// Public (no auth required)
// EP-EXTACCESS Slice 3: the legacy DocumentRequestPage (unredacted 24h
// share flow) is retired — printed QR signs land on /documents/{token}
// and get forwarded to the request-access intake.
const LegacyDocumentsRedirect = lazyNamed(
  () => import('./pages/public/LegacyShareRetired'),
  'LegacyDocumentsRedirect'
);
const LegacyShareRetiredPage = lazyNamed(
  () => import('./pages/public/LegacyShareRetired'),
  'LegacyShareRetiredPage'
);
const PIFSubmissionPage = lazyNamed(
  () => import('./pages/public/PIFSubmissionPage'),
  'PIFSubmissionPage'
);
const SwpppSignPage = lazyNamed(
  () => import('./pages/public/SwpppSignPage'),
  'SwpppSignPage'
);
const FinalConfirmPage = lazyNamed(
  () => import('./pages/public/FinalConfirmPage'),
  'FinalConfirmPage'
);
const SignReportPage = lazyNamed(
  () => import('./pages/public/SignReportPage'),
  'SignReportPage'
);
const MapUploadPage = lazyNamed(
  () => import('./pages/public/MapUploadPage'),
  'MapUploadPage'
);
const ExternalSiteViewPage = lazyNamed(
  () => import('./pages/public/ExternalSiteViewPage'),
  'ExternalSiteViewPage'
);
const SiteShortLinkPage = lazyNamed(
  () => import('./pages/public/SiteShortLinkPage'),
  'SiteShortLinkPage'
);
const RequestAccessPage = lazyNamed(
  () => import('./pages/public/RequestAccessPage'),
  'RequestAccessPage'
);
const ConfirmRequestPage = lazyNamed(
  () => import('./pages/public/ConfirmRequestPage'),
  'ConfirmRequestPage'
);
const SiteDocuments = lazyNamed(
  () => import('./pages/SiteDocuments'),
  'SiteDocuments'
);

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/login" replace />, // Go directly to login
      },
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'signup',
        element: <SignupPage />,
      },
      {
        path: 'verify-email',
        element: <EmailVerificationPage />,
      },
      {
        path: 'pending-approval',
        element: <PendingApprovalPage />,
      },
      {
        path: 'auth/callback',
        element: <OIDCCallbackPage />,
      },
      {
        path: 'accept-invite',
        element: <AcceptInvitePage />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: 'reset-password',
        element: <ResetPasswordPage />,
      },
      // JIM-0924-02: the external-user access tunnel. Both doors are public —
      // the QR sticker on a site and this lookup page — and neither reveals
      // whether an address is on file.
      {
        path: 'site-access',
        element: <SiteAccessStartPage />,
      },
      // JIM-0929-04: the sticker fallback — location + the customer name off
      // the notice, resolved silently. Never a list of sites.
      {
        path: 'site-access/find',
        element: <SiteAccessFindPage />,
      },
      {
        path: 'set-up-access',
        element: <SiteAccessSetupPage />,
      },
      // EP-EXTACCESS Slice 3: printed QR signs resolve here — forward
      // the site token to the gated request-access intake. Old emailed
      // 24h share links (/documents/download/{token}) get the retired
      // notice; they no longer resolve to documents.
      {
        path: 'documents/download/:shareToken',
        element: <LegacyShareRetiredPage />,
      },
      {
        path: 'documents/:token',
        element: <LegacyDocumentsRedirect />,
      },
      {
        path: 'pif/submit/:token',
        element: <PIFSubmissionPage />,
      },
      {
        path: 'sign/:token',
        element: <SwpppSignPage />,
      },
      {
        path: 'confirm-final/:token',
        element: <FinalConfirmPage />,
      },
      {
        path: 'sign-report/:token',
        element: <SignReportPage />,
      },
      // EP-MAPPRODUCER: the map producer's single-use BMP-map upload
      {
        path: 'map-upload/:token',
        element: <MapUploadPage />,
      },
      // EP-EXTACCESS: external approved-access view-only portal
      {
        path: 'view/:token',
        element: <ExternalSiteViewPage />,
      },
      // JIM-0919-10: the sticker's typed link (/s/XXXX-XXXX) opens the
      // same intake the QR opens
      {
        path: 's/:code',
        element: <SiteShortLinkPage />,
      },
      // EP-EXTACCESS Slice 2: public request-access intake + email confirm
      {
        path: 'request-access/:token',
        element: <RequestAccessPage />,
      },
      {
        path: 'confirm-request/:token',
        element: <ConfirmRequestPage />,
      },
      // QR Code Public Routes (no auth required)
      {
        path: 'site/:siteId/documents',
        element: <SiteDocuments />,
      },
      {
        // Where an external user lands once they have a password.
        path: 'my-sites',
        element: (
          <ProtectedRoute>
            <ClientHomePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'select-mode',
        element: (
          <ProtectedRoute>
            <ModeSelectionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <DashboardPage />,
          },
          {
            path: 'clients',
            children: [
              {
                index: true,
                element: <OrganizationsPage />,
              },
              {
                path: ':clientId',
                element: <ClientDetailPage />,
              },
            ],
          },
          {
            path: 'inspections',
            children: [
              {
                index: true,
                element: <Inspections />,
              },
              {
                // Conducting inspections is Lite-only (Chris 2026-08-20);
                // the old desktop create URL walks users across.
                path: 'new',
                element: <InspectionLiteHandoff />,
              },
              {
                // Read-only record — the desktop conducting form is gone
                // (inspections are conducted in EnviroLite only).
                path: ':inspectionId',
                element: <InspectionRecordPage />,
              },
            ],
          },
          {
            path: 'sites',
            children: [
              {
                index: true,
                element: <SitesPage />,
              },
              {
                path: 'new',
                element: <NewSite />,
              },
              {
                path: ':siteId',
                element: <SiteDetailPage />,
              },
            ],
          },
          {
            path: 'schedule',
            element: <SchedulePage />,
          },
          {
            path: 'reports',
            element: <ReportsPage />,
          },
          {
            path: 'reports/data-quality',
            element: <DataQualityPage />,
          },
          {
            path: 'reports/setup-worklist',
            element: <SetupWorklistPage />,
          },
          {
            path: 'reports/permit-expirations',
            element: <PermitExpirationsPage />,
          },
          {
            path: 'settings',
            element: <SettingsPage />,
          },
          // People Hub (top-level for user management)
          {
            path: 'people',
            element: <PeopleHubPage />,
          },
          // Unified Admin Portal (new consolidated interface)
          {
            path: 'admin',
            element: <AdminPortal />,
          },
          // Standalone admin routes still linked from live UI (invite flow);
          // the rest of the old admin/* routes were retired with
          // AdminOverviewPage — their panels live inside AdminPortal now.
          {
            path: 'admin/invite',
            element: <InviteUserPage />,
          },
          {
            path: 'admin/invitations',
            element: <InvitationsListPage />,
          },
          {
            path: 'admin/swppp-plans/:planId',
            element: <SWPPPPlanEditorPage />,
          },
          {
            path: 'financials',
            element: <FinancialDashboard />,
          },
          {
            path: 'consultant',
            element: <ConsultantDashboard />,
          },
          {
            path: 'client-management',
            element: <ClientManagementPage />,
          },
          {
            path: 'recon',
            element: <DataReconInboxPage />,
          },
        ],
      },
      {
        path: 'lite',
        element: (
          <ProtectedRoute>
            <LiteLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <LiteDashboard />,
          },
          {
            path: 'sites',
            children: [
              {
                index: true,
                element: <LiteSitesPage />,
              },
              {
                path: ':siteId',
                element: <LiteSiteDetailPage />,
              },
            ],
          },
          {
            // Actions merged into Schedule (2026-06); redirect old links/bookmarks.
            path: 'actions',
            element: <Navigate to="/lite/schedule" replace />,
          },
          {
            path: 'schedule',
            element: <LiteSchedulePage />,
          },
          {
            path: 'maps',
            element: <LiteMapsPage />,
          },
          {
            path: 'learn',
            element: <LiteLearnPage />,
          },
          {
            path: 'settings',
            element: <SettingsPage />,
          },
          {
            path: 'start-inspection',
            element: <LiteStartInspectionPage />,
          },
          {
            path: 'inspect/:siteId',
            element: <LiteInspectionFlowPage />,
          },
          {
            // JIM-0904-11: attach the state/city form instead of answering
            // questions — stages + corrective actions still recorded.
            path: 'inspect/:siteId/upload',
            element: <LiteManualUploadPage />,
          },
        ],
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
